import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { ApiClient } from '@core/services/api-client.http.service';
import { PromoPageUrls, ProductUrls } from '@core/constants/endpoints.constants';
import {
  PromoPageCreateRequest,
  PromoPageUpdateRequest,
  PromoPagesDto,
  PromoPagesDtoResponse
} from '@core/services/dto/promo-page.dto';
import { ProductApiResponse } from '@core/services/dto/product-api.dto';
import { ProductCategoryApiResponse } from '@core/services/dto/product-category-api.dto';
import { PaginatedResponseDto } from '@core/services/dto';

@Injectable({
  providedIn: 'any'
})
export class PromoPageApiService {
  constructor(private api: ApiClient) {}

  /**
   * Get all promo pages with pagination
   */
  getPromoPages(request?: any): Observable<PaginatedResponseDto<PromoPagesDto>> {
    return this.api.get<PaginatedResponseDto<PromoPagesDto>>(PromoPageUrls.promoPages, request);
  }

  /**
   * Get a specific promo page by ID
   */
  getPromoPage(id: number): Observable<PromoPagesDtoResponse> {
    return this.api.get<PromoPagesDtoResponse>(PromoPageUrls.promoPage(id));
  }

  /**
   * Create a new promo page
   */
  createPromoPage(request: PromoPageCreateRequest): Observable<PromoPagesDto> {
    const formData = this.createFormData(request);
    return this.api.post<PromoPagesDto>(PromoPageUrls.promoPages, formData);
  }

  /**
   * Update an existing promo page
   */
  updatePromoPage(request: PromoPageUpdateRequest): Observable<PromoPagesDto> {
    const formData = this.createFormData(request);
    return this.api.post<PromoPagesDto>(PromoPageUrls.promoPages, formData);
  }

  /**
   * Delete a promo page
   */
  deletePromoPage(id: number): Observable<any> {
    return this.api.delete<any>(PromoPageUrls.promoPage(id));
  }

  /**
   * Delete a promo page
   */
  deletePromoPageBanner(
    id: number,
    bannerURL: string,
    bannerType: 'PromoBanner' | 'MobileBanner',
    langCode?: string
  ): Observable<any> {
    const payload = {
      id,
      bannerURL,
      bannerType,
      languageCode: langCode
    };

    return this.api.delete<any>(PromoPageUrls.deletePromoBanner, payload);
  }



  /**
   * Get all products from the external API
   */
  getProducts(categoryId: number): Observable<ProductApiResponse> {
    return this.api.get<ProductApiResponse>(ProductUrls.externalProducts(categoryId));
  }


  /**
   * Get all product categories from the external API
   */
  getProductCategories(): Observable<ProductCategoryApiResponse> {
    return this.api.get<ProductCategoryApiResponse>(ProductUrls.externalProductCategories);
  }

  /**
   * Create FormData from the request object for file uploads
   */
  private createFormData(request: PromoPageCreateRequest | PromoPageUpdateRequest): FormData {
    const formData = new FormData();

    if (request.id) {
      formData.append('Id', request.id.toString());
    }
    formData.append('Name', request.name);
    formData.append('URL', request.url);
    formData.append('Title', request.title);
    formData.append('Content', request.content);
    formData.append('Status', request.status);
    formData.append('StartDate', request.startDate);
    if (request.endDate) {
      formData.append('EndDate', request.endDate);
    }

    if (request.bannerImage) {
      formData.append('BannerImage', request.bannerImage, request.bannerImage.name);
    }
    if (request.mobileBannerImage) {
      formData.append('MobileBannerImage', request.mobileBannerImage, request.mobileBannerImage.name);
    }

    if (request.product) {
      formData.append('ProductJson', JSON.stringify(request.product));
    }

    return formData;
  }

  private createFormDataForTranslation(request: {
  id?: number;
  promoPageId: number;
  languageCode: string;
  title?: string;
  content?: string;
  productSelectionTitle?: string;
  bannerImage?: File;
  mobileBannerImage?: File;
  productJson?: string;
  product?: any[];
}): FormData {
  const formData = new FormData();

  if (request.id) {
    formData.append('Id', request.id.toString());
  }
  formData.append('PromoPageId', request.promoPageId.toString());
  formData.append('LanguageCode', request.languageCode);

  if (request.title) {
    formData.append('Title', request.title);
  }
  if (request.content) {
    formData.append('Content', request.content);
  }
  if (request.productSelectionTitle) {
    formData.append('ProductSelectionTitle', request.productSelectionTitle);
  }

  if (request.bannerImage) {
    formData.append('BannerImage', request.bannerImage, request.bannerImage.name);
  }
  if (request.mobileBannerImage) {
    formData.append('MobileBannerImage', request.mobileBannerImage, request.mobileBannerImage.name);
  }

  if (request.productJson) {
    formData.append('ProductJson', request.productJson);
  } else if (request.product) {
    formData.append('ProductJson', JSON.stringify(request.product));
  }

  return formData;
}



  getList(request?: any): Observable<PaginatedResponseDto<PromoPagesDto>> {
    return this.api.get<any>(PromoPageUrls.promoPages, request).pipe(
      map((res: any) => {
        console.log('Raw API response:', res);
        console.log('Request parameters:', request);

        const mappedResponse = {
          data: res?.result ?? res?.data ?? [],
          hasNextPage: res?.hasNextPage ?? false,
          hasPreviousPage: res?.hasPreviousPage ?? false,
          pageNumber: res?.pageNumber ?? request?.pageNumber ?? 1,
          totalCount: res?.totalCount ?? res?.totalRecords ?? res?.count ?? 0,
          totalPages: res?.totalPages ?? Math.ceil((res?.totalCount ?? res?.totalRecords ?? res?.count ?? 0) / (request?.pageSize ?? 10))
        };

        console.log('Mapped response:', mappedResponse);
        return mappedResponse;
      })
    );
  }

  addOrUpdatePromoPageLocalization(localization: any): Observable<boolean> {
    const formData = this.createFormDataForTranslation(localization);
    return this.api.post<boolean>(PromoPageUrls.promoPageTranslation, formData);
  }

  getLocalization(id: number, languageCode: string): Observable<any> {
    return this.api.get<boolean>(PromoPageUrls.promoPageLocalization(id, languageCode));
  }
}
